/**
 * JustOnAir hosted player - play.joacdn.com/{stream_id} or /{channel_id}
 *
 * One page, embedded in an iframe by customers (or opened as a share link).
 * Everything it knows comes from GET /v1/embed/{id}: the status and, while
 * live, a signed HLS URL. Views:
 *
 *   loading → pending (waiting room) → starting (first segments on their way)
 *           → live → ended → replay (when the owner turned replay on)
 *   also: unavailable (expired/cancelled), closed (the owner's watch-time
 *   budget ran out; the stream may go on elsewhere), notfound, offline
 *
 * The title is the stream's name (unless its owner set player.show_name =
 * false, then the API sends none). Options on the iframe URL (all optional):
 *   ?title=Friday%20yoga   use this title instead of the stream's name
 *   ?accent=ff7a00         replaces the JustOnAir lime (6 hex digits)
 *   ?muted=1               start muted
 *   ?autoplay=0            wait for a click instead of starting on its own
 *   ?chat=0                no chat even when the stream has it; ?chat=1 opens it
 *                          at once in an embed (it is a button there otherwise)
 *   ?lang=tr|en            chat language (default: the browser's)
 *
 * A channel page (/chn_...) shows the channel's stream on air, or its
 * waiting page between streams; when one stream gives way to the next
 * (or ends) the page reloads, so player, chat and counting start clean.
 *
 * Chat (docs/viewer-chat-plan.md) is a separate chunk, fetched only when the
 * embed read says the stream has chat. Opened as a page, it sits beside the
 * video (below it on phones); inside someone's iframe it is a panel that a
 * button slides over the video.
 */
import '@fontsource-variable/hanken-grotesk/wght.css';
import './styles.css';
import type HlsType from 'hls.js/light';
import type { ErrorData, HlsConfig, Level } from 'hls.js/light';
import { icons } from './icons';
import logo from './logo.svg?raw';
import { takeViewerToken } from './viewer-token';

type Status = 'pending' | 'live' | 'ended' | 'unavailable' | 'closed';
interface Embed {
  id: string;
  status: Status;
  title: string | null;
  playback_url: string | null;
  /** Offer no rendition taller than this (player.max_height); null = all. */
  max_height?: number | null;
  went_live_at: string | null;
  ended_at: string | null;
  poll_after_seconds: number | null;
  beacon: { rate: number; interval_seconds: number } | null;
  /** The stream's newest frame, when its owner made thumbnails public. */
  poster_url?: string | null;
  /** Viewer chat; null when the stream has none. */
  chat?: { enabled: true; reactions: boolean; state: 'open' | 'paused' | 'read_only' | 'closed' | 'off' } | null;
  /** Read by channel id: the channel and its next planned stream. */
  channel?: { id: string; scheduled_at: string | null } | null;
  /** After the end, with replay on and the recording ready: the MP4 to play (null url: members get their own). */
  replay?: { url: string | null; expires_at: string | null; duration_seconds: number | null } | null;
  /** `viewer_token`: members only; each viewer's URLs come from their session (docs: /viewer-tokens). */
  access?: 'public' | 'viewer_token';
}
type View =
  | 'loading'
  | 'pending'
  | 'starting'
  | 'live'
  | 'ended'
  | 'replay'
  | 'unavailable'
  | 'closed'
  | 'notfound'
  | 'offline'
  | 'unsupported'
  | 'members'
  | 'removed';

const API = (import.meta.env.VITE_API_URL || 'https://api.justonair.com').replace(/\/$/, '');
// The status read goes through the CDN on this same host (cached 5 s at the
// edge, so a 50,000-viewer waiting room costs the API one request per 5 s).
// Local dev points it straight at an API.
const EMBED_READ = (import.meta.env.VITE_EMBED_URL || '/api/embed').replace(/\/$/, '');
const BRAND_URL = 'https://justonair.com/?ref=player';

// ---------------------------------------------------------------- options
const params = new URLSearchParams(location.search);
/** What the page is for: a stream id, or a channel id. */
const pageId = decodeURIComponent(location.pathname.split('/').filter(Boolean)[0] ?? '') || params.get('v') || '';
// The site's viewer token (#vt=), taken off the address at once.
const viewerToken = takeViewerToken(pageId);
const isChannel = /^chn_[a-z0-9]{8,40}$/.test(pageId);
/** The stream being watched: the page's own, or the channel's stream on air. */
let streamId = pageId;
/** On a channel page: the channel's stream this page load is showing, once one began. */
let channelStreamId: string | null = null;
const opts = {
  title: (params.get('title') ?? '').trim().slice(0, 120),
  accent: /^[0-9a-f]{6}$/i.test(params.get('accent') ?? '') ? `#${params.get('accent')}` : null,
  muted: params.get('muted') === '1',
  autoplay: params.get('autoplay') !== '0',
  chat: params.get('chat'),
  reactions: params.get('reactions') !== '0',
  lang: params.get('lang'),
};
/** In someone else's page (an iframe) rather than opened as a page of its own. */
const framed = (() => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
})();

// ---------------------------------------------------------------- DOM
const root = document.getElementById('player')!;
if (opts.accent) {
  root.style.setProperty('--accent', opts.accent);
  root.style.setProperty('--accent-ink', inkFor(opts.accent));
}

root.innerHTML = `
  <video class="jp-video" playsinline webkit-playsinline preload="auto" disablepictureinpicture></video>
  <canvas class="jp-freeze" aria-hidden="true"></canvas>
  <div class="jp-hit"></div>
  <section class="jp-stage" aria-live="polite">
    <div class="jp-aurora" aria-hidden="true"><i></i><i></i><i></i></div>
    <div class="jp-poster" aria-hidden="true"></div>
    <div class="jp-dots" aria-hidden="true"></div>
    <div class="jp-card">
      <div class="jp-beacon" aria-hidden="true"><span></span><span></span><span></span><b></b></div>
      <div class="jp-eyebrow"></div>
      <h1 class="jp-headline"></h1>
      <p class="jp-sub"></p>
      <button class="jp-replay" type="button">${icons.play}<span>Watch the replay</span></button>
    </div>
    <a class="jp-mark" href="${BRAND_URL}" target="_blank" rel="noopener" aria-label="JustOnAir"><i></i>${logo}</a>
  </section>
  <div class="jp-spinner" aria-hidden="true"></div>
  <div class="jp-top"><div class="jp-top-title"></div></div>
  <div class="jp-notice" role="status"></div>
  <button class="jp-unmute" type="button">${icons.muted}<span>Tap to unmute</span></button>
  <button class="jp-chatbtn" type="button" data-act="chat" aria-label="Chat">${icons.chat}<span>Chat</span></button>
  <div class="jp-reactbar"></div>
  <div class="jp-floatlayer"></div>
  <button class="jp-bigplay" type="button" aria-label="Play">${icons.play}</button>
  <div class="jp-bar">
    <div class="jp-bar-row">
      <button class="jp-btn" data-act="play" type="button" aria-label="Play">${icons.play}</button>
      <div class="jp-vol">
        <button class="jp-btn" data-act="mute" type="button" aria-label="Mute">${icons.volume}</button>
        <input class="jp-slider" type="range" min="0" max="1" step="0.02" value="1" aria-label="Volume" />
      </div>
      <button class="jp-live" type="button" data-act="golive" aria-label="Jump to live"><i></i><span>LIVE</span></button>
      <span class="jp-clock" aria-label="Time live"></span>
      <span class="jp-grow"></span>
      <div class="jp-qwrap">
        <button class="jp-btn jp-q" data-act="quality" type="button" aria-label="Quality" aria-haspopup="menu" aria-expanded="false">${icons.quality}<em></em></button>
        <div class="jp-menu" role="menu"></div>
      </div>
      <button class="jp-btn" data-act="pip" type="button" aria-label="Picture in picture">${icons.pip}</button>
      <button class="jp-btn" data-act="fullscreen" type="button" aria-label="Full screen">${icons.fullscreen}</button>
      <a class="jp-brand" href="${BRAND_URL}" target="_blank" rel="noopener" aria-label="JustOnAir"><i></i><span>${logo}</span></a>
    </div>
  </div>`;

const $ = <T extends Element = HTMLElement>(sel: string) => root.querySelector(sel) as unknown as T;
const video = $<HTMLVideoElement>('.jp-video');
const eyebrow = $('.jp-eyebrow');
const headline = $('.jp-headline');
const sub = $('.jp-sub');
const notice = $('.jp-notice');
const clock = $('.jp-clock');
const slider = $<HTMLInputElement>('.jp-slider');
const menu = $('.jp-menu');
const freeze = $<HTMLCanvasElement>('.jp-freeze');
const poster = $('.jp-poster');
const qLabel = $('.jp-q em');
const btn = (act: string) => $<HTMLButtonElement>(`[data-act="${act}"]`);

/** ?title= wins; otherwise the stream's name, when its owner shows it. */
const title = () => opts.title || embed?.title || '';
let shownTitle: string | null = null;
function applyTitle() {
  const t = title();
  if (t === shownTitle) return;
  shownTitle = t;
  $('.jp-top-title').textContent = t;
  root.classList.toggle('has-title', !!t);
  document.title = t ? `${t} · Live` : 'Live · JustOnAir';
  if (view !== 'live' && view !== 'loading') setView(view, true);
}

// ---------------------------------------------------------------- state
let view: View = 'loading';
let embed: Embed | null = null;
let hls: HlsType | null = null;
let native = false;
let pollTimer = 0;
let retryTimer = 0;
let failures = 0;
let restarts = 0;
let hasPlayed = false;
/** The API says the stream ended; let what's buffered play out first. */
let endPending = false;
let stallTimer = 0;
let spinTimer = 0;

let HlsCtor: typeof HlsType | null = null;
let hlsLoading: Promise<typeof HlsType> | null = null;
function loadHls(): Promise<typeof HlsType> {
  hlsLoading ??= import('hls.js/light').then((m) => (HlsCtor = m.default));
  return hlsLoading;
}

// ---------------------------------------------------------------- views
const COPY: Record<View, [string, string, string]> = {
  // eyebrow, headline (when there is no ?title), sub
  loading: ['', '', ''],
  pending: ['Starting soon', 'The stream will begin shortly', 'This page starts playing by itself. No need to refresh.'],
  starting: ['Going live', 'Here we go', 'Connecting to the stream…'],
  live: ['', '', ''],
  ended: ['Stream ended', 'That’s a wrap', 'Thanks for watching.'],
  replay: ['', '', ''],
  unavailable: ['Unavailable', 'This stream isn’t available', 'It was cancelled or never started.'],
  closed: ['Watch page closed', 'This watch page has closed', 'The stream may continue on the host’s other platforms.'],
  notfound: ['Not found', 'We couldn’t find this stream', 'Check the link you were given.'],
  offline: ['Connection', 'Can’t reach the stream', 'Retrying automatically…'],
  unsupported: ['Browser', 'This browser can’t play live video', 'Try a recent Chrome, Safari, Firefox or Edge.'],
  members: ['Members only', 'This stream is for members', 'Open it from the site where you signed in.'],
  removed: ['Access ended', 'Your access to this stream ended', 'Open it again from the site where you signed in.'],
};

function setView(v: View, force = false) {
  if (v === view && !force) return;
  view = v;
  root.dataset.view = v;
  const [e, h, s] = COPY[v];
  eyebrow.textContent = e;
  const t = title();
  const titled = !!t && (v === 'pending' || v === 'starting' || v === 'ended');
  headline.textContent = titled ? t : h;
  let subText = titled && v === 'ended' ? 'That’s a wrap. Thanks for watching.' : s;
  if (v === 'ended') {
    const d = durationText();
    if (d) subText = `Live for ${d}. ${subText.replace('That’s a wrap. ', '')}`;
  }
  sub.textContent = subText;
  if (v !== 'live') {
    showNotice(null);
    root.classList.remove('is-unmute', 'is-bigplay', 'is-spinning', 'is-frozen');
  }
  if (v === 'pending' && isChannel) {
    // A channel between streams: the waiting page, with the next planned one.
    const at = embed?.channel?.scheduled_at ? new Date(embed.channel.scheduled_at) : null;
    const soon = at && at.getTime() > Date.now() - 15 * 60_000;
    eyebrow.textContent = soon ? 'Next stream' : 'Offline';
    if (!titled) headline.textContent = soon ? 'Coming up' : 'Nothing on air right now';
    sub.textContent = soon
      ? `${new Intl.DateTimeFormat(undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).format(at!)}. This page starts playing by itself.`
      : 'This page starts playing by itself when the next stream begins.';
  }
  if (v === 'ended' || v === 'unavailable' || v === 'closed' || v === 'notfound') {
    beacon.stop();
    teardown();
    clearTimeout(pollTimer);
    clearTimeout(retryTimer);
    // A channel goes on after a stream ends: keep asking, and the waiting page
    // (or the next stream) replaces this one.
    if (isChannel && v === 'ended') schedule(15);
  }
  if (v === 'live') uiPing();
}

function durationText(): string | null {
  if (!embed?.went_live_at) return null;
  const end = embed.ended_at ? Date.parse(embed.ended_at) : Date.now();
  const mins = Math.max(1, Math.round((end - Date.parse(embed.went_live_at)) / 60000));
  const h = Math.floor(mins / 60);
  return h ? `${h} h ${String(mins % 60).padStart(2, '0')} min` : `${mins} min`;
}

function showNotice(text: string | null) {
  notice.textContent = text ?? '';
  root.classList.toggle('is-notice', !!text);
}

// ---------------------------------------------------------------- status
async function fetchEmbed(): Promise<Embed | 'notfound' | null> {
  if (!/^(str|chn)_[a-z0-9]{8,40}$/.test(pageId)) return 'notfound';
  try {
    const ctl = new AbortController();
    const t = setTimeout(() => ctl.abort(), 8000);
    const r = await fetch(`${EMBED_READ}/${pageId}`, { credentials: 'omit', signal: ctl.signal });
    clearTimeout(t);
    if (r.status === 404) return 'notfound';
    if (!r.ok) return null;
    return (await r.json()) as Embed;
  } catch {
    return null;
  }
}

function schedule(seconds: number) {
  clearTimeout(pollTimer);
  // Jitter: a thousand viewers who opened the page together must not ask together forever.
  pollTimer = window.setTimeout(tick, (seconds + Math.random() * Math.min(3, seconds / 2)) * 1000);
}

async function tick() {
  clearTimeout(pollTimer);
  const e = await fetchEmbed();
  if (e === 'notfound') return setView('notfound');
  if (!e) {
    failures++;
    // A playing viewer doesn't care that the API blinked; the video comes from the CDN.
    if (view !== 'live') setView('offline');
    return schedule(Math.min(30, 2 ** Math.min(failures, 5)));
  }
  failures = 0;
  if (isChannel) {
    const next = e.id.startsWith('str_') ? e.id : null;
    // The stream this page was showing is over, or another one began:
    // start clean rather than carry one stream's player, chat and count into the next.
    if (channelStreamId && next !== channelStreamId) return location.reload();
    if (next) channelStreamId = streamId = next;
  }
  embed = e;
  applyTitle();
  applyPoster(e.poster_url ?? null);
  // Signed in by the site (#vt=): who this viewer is, for chat and attendance,
  // and on a members-only stream their own playback URLs.
  await member.prepare(e);
  if (e.access === 'viewer_token' && !member.playback && (e.status === 'live' || e.status === 'pending' || e.status === 'ended')) {
    teardown();
    setView(member.refused ? 'removed' : 'members');
    return schedule(e.poll_after_seconds ?? 60);
  }
  beacon.update(e);
  chat.update(e);
  switch (e.status) {
    case 'pending':
      setView('pending');
      // Fetch the player engine while people wait, so going live is instant.
      if ('requestIdleCallback' in window) requestIdleCallback(() => void loadHls());
      else setTimeout(() => void loadHls(), 500);
      break;
    case 'live': {
      // Signed in while it was still waiting: this viewer's live URL now.
      if (e.access === 'viewer_token' && !member.playback?.url) await member.refresh();
      const url = e.access === 'viewer_token' ? member.playback?.url : e.playback_url;
      if (!hls && !native && url) await start(url);
      break;
    }
    case 'ended':
      if (e.access === 'viewer_token' && e.replay && !member.playback?.replay_url) await member.refresh();
      // The replay button appears once the recording is ready (replay on).
      root.classList.toggle('has-replay', !!e.replay && !!(e.replay.url ?? member.playback?.replay_url));
      if (view === 'replay') break;
      if (view === 'live' && hasPlayed && !video.paused) {
        endPending = true; // play the buffered tail; 'ended' or a stall finishes it
        window.setTimeout(() => endPending && setView('ended'), 45_000);
      } else setView('ended');
      break;
    case 'unavailable':
      setView('unavailable');
      break;
    case 'closed':
      // No tail to play out: the page is closed now, not when the stream ends.
      endPending = false;
      setView('closed');
      break;
  }
  if (e.poll_after_seconds) schedule(e.poll_after_seconds);
}

// ---------------------------------------------------------------- playback
async function start(url: string) {
  if (view !== 'live') setView('starting');
  if (!('MediaSource' in window || 'ManagedMediaSource' in window) && video.canPlayType('application/vnd.apple.mpegurl')) {
    native = true;
    video.src = url;
    video.load();
    return;
  }
  const Hls = HlsCtor ?? (await loadHls().catch(() => null));
  if (!Hls || !Hls.isSupported()) {
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      native = true;
      video.src = url;
      video.load();
    } else setView('unsupported');
    return;
  }
  if (hls) return;
  const h = new Hls({
    // Members only: every request carries this viewer's newest token, so a
    // renewed URL takes over without restarting playback.
    ...(embed?.access === 'viewer_token' ? { loader: tokenLoader(Hls.DefaultConfig.loader) } : {}),
    // 4 s segments: start three behind the edge (~12 s), and drift back to
    // that target by speeding up slightly rather than jumping.
    liveSyncDurationCount: 3,
    liveMaxLatencyDurationCount: 10,
    maxLiveSyncPlaybackRate: 1.08,
    backBufferLength: 30,
    capLevelToPlayerSize: true,
    manifestLoadingMaxRetry: 1,
    manifestLoadingRetryDelay: 1000,
    levelLoadingMaxRetry: 4,
    fragLoadingMaxRetry: 6,
  });
  hls = h;
  h.on(Hls.Events.MANIFEST_PARSED, () => {
    capLevels(h);
    buildQualityMenu();
    void autoplay();
  });
  h.on(Hls.Events.LEVEL_SWITCHED, updateQualityLabel);
  h.on(Hls.Events.ERROR, (_e, d: ErrorData) => {
    if (!d.fatal) return;
    if (d.type === Hls.ErrorTypes.MEDIA_ERROR && restarts < 3) {
      restarts++;
      h.recoverMediaError();
      return;
    }
    recover();
  });
  h.loadSource(url);
  h.attachMedia(video);
}

/**
 * Drop renditions taller than the owner's cap (player.max_height), tallest
 * index first so the remaining indexes stay valid. Keeps at least one. Safari's
 * native HLS path can't be capped this way; it is a cost cap, not a lock.
 */
function capLevels(h: HlsType) {
  const cap = embed?.max_height;
  if (!cap) return;
  const over = h.levels
    .map((l, i) => ({ h: l.height, i }))
    .filter((x) => x.h > cap)
    .map((x) => x.i)
    .sort((a, b) => b - a);
  if (over.length >= h.levels.length) over.pop(); // never remove every level (the smallest stays)
  for (const i of over) h.removeLevel(i);
}

function teardown() {
  clearTimeout(stallTimer);
  if (hls) {
    hls.destroy();
    hls = null;
  }
  if (native) {
    video.removeAttribute('src');
    video.load();
    native = false;
  }
}

/** Something broke: find out from the API whether the stream is still on, then start again. */
function recover() {
  if (view === 'live') freezeFrame();
  teardown();
  if (endPending) return setView('ended');
  if (view === 'live') {
    showNotice('Reconnecting…');
    spin(true);
  }
  clearTimeout(retryTimer);
  const wait = Math.min(8, 1.5 * 2 ** Math.min(restarts, 3));
  restarts++;
  retryTimer = window.setTimeout(tick, wait * 1000);
}

async function autoplay() {
  if (!opts.autoplay && !hasPlayed) {
    setView('live');
    root.classList.add('is-bigplay');
    return;
  }
  video.muted = opts.muted || prefs.muted;
  video.volume = prefs.volume;
  try {
    await video.play();
  } catch {
    if (!video.muted) {
      // Browsers block sound until the viewer interacts. Muted playback is allowed;
      // say so plainly and let one tap bring the sound back.
      video.muted = true;
      try {
        await video.play();
        root.classList.add('is-unmute');
        return;
      } catch {
        /* fall through */
      }
    }
    setView('live');
    root.classList.add('is-bigplay');
  }
}

video.addEventListener('loadeddata', () => {
  if (view === 'starting') setView('live');
});
video.addEventListener('playing', () => {
  if (view === 'replay') return;
  hasPlayed = true;
  restarts = 0;
  setView('live');
  showNotice(null);
  spin(false);
  root.classList.remove('is-bigplay', 'is-frozen');
  clearTimeout(stallTimer);
  syncButtons();
});
video.addEventListener('pause', syncButtons);
video.addEventListener('play', syncButtons);
video.addEventListener('volumechange', syncButtons);
video.addEventListener('waiting', () => {
  if (view === 'replay') return; // the browser's own controls show buffering
  spin(true);
  clearTimeout(stallTimer);
  stallTimer = window.setTimeout(() => {
    if (endPending) return setView('ended');
    // Ten seconds without a frame: the publisher dropped, or the network did.
    recover();
  }, 10_000);
});
video.addEventListener('ended', () => {
  if (view === 'live') setView('ended');
});
video.addEventListener('error', () => {
  if (native) recover();
});

/**
 * The stream's newest frame (thumbnail.public): the video's poster before the
 * first frame plays, and a blurred backdrop behind "starting" and "ended".
 * Loaded first and swapped in only once it has arrived, so a missing or
 * expired frame leaves the usual backdrop rather than a broken image.
 */
let posterShown: string | null = null;
function applyPoster(url: string | null) {
  if (url === posterShown) return;
  if (!url) {
    posterShown = null;
    video.removeAttribute('poster');
    root.classList.remove('has-poster');
    return;
  }
  const img = new Image();
  img.onload = () => {
    if (embed?.poster_url !== url) return; // a newer answer arrived meanwhile
    posterShown = url;
    video.poster = url;
    poster.style.backgroundImage = `url("${url}")`;
    root.classList.add('has-poster');
  };
  img.src = url;
}

/** Keep the last picture (blurred) on screen while we reconnect, instead of black. */
function freezeFrame() {
  if (!video.videoWidth || root.classList.contains('is-frozen')) return;
  try {
    freeze.width = 320;
    freeze.height = Math.round((320 * video.videoHeight) / video.videoWidth);
    freeze.getContext('2d')?.drawImage(video, 0, 0, freeze.width, freeze.height);
    root.classList.add('is-frozen');
  } catch {
    /* nothing to show; black it is */
  }
}

function spin(on: boolean) {
  clearTimeout(spinTimer);
  if (!on) return root.classList.remove('is-spinning');
  // Short hiccups shouldn't flash a spinner.
  spinTimer = window.setTimeout(() => root.classList.add('is-spinning'), 350);
}

// ---------------------------------------------------------------- preferences
const prefs = { volume: 1, muted: false };
try {
  const saved = JSON.parse(localStorage.getItem('jp.prefs') ?? '{}');
  if (typeof saved.volume === 'number') prefs.volume = Math.min(1, Math.max(0, saved.volume));
  if (typeof saved.muted === 'boolean') prefs.muted = saved.muted;
} catch {
  /* storage blocked in third-party iframes: defaults are fine */
}
function savePrefs() {
  prefs.volume = video.volume;
  prefs.muted = video.muted;
  try {
    localStorage.setItem('jp.prefs', JSON.stringify(prefs));
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------- controls
function togglePlay() {
  if (video.paused) {
    root.classList.remove('is-bigplay');
    // Resuming after a pause lands at the live edge, not minutes behind it.
    if (hasPlayed && behindLive() > 20) goLive();
    void video.play().catch(() => root.classList.add('is-bigplay'));
  } else video.pause();
}

function toggleMute() {
  video.muted = !video.muted;
  if (!video.muted && video.volume === 0) video.volume = 0.6;
  root.classList.remove('is-unmute');
  savePrefs();
}

function liveEdge(): number | null {
  if (hls?.liveSyncPosition != null) return hls.liveSyncPosition;
  const s = video.seekable;
  return s.length ? s.end(s.length - 1) - 12 : null;
}
function behindLive(): number {
  const edge = liveEdge();
  return edge == null ? 0 : Math.max(0, edge - video.currentTime);
}
function goLive() {
  const edge = liveEdge();
  if (edge != null) video.currentTime = edge;
  if (video.paused) void video.play().catch(() => undefined);
}

function toggleFullscreen() {
  const d = document as Document & { webkitFullscreenElement?: Element; webkitExitFullscreen?: () => void };
  if (d.fullscreenElement || d.webkitFullscreenElement) {
    (d.exitFullscreen ?? d.webkitExitFullscreen)?.call(d);
    return;
  }
  const r = root as HTMLElement & { webkitRequestFullscreen?: () => void };
  if (r.requestFullscreen) void r.requestFullscreen().catch(() => undefined);
  else if (r.webkitRequestFullscreen) r.webkitRequestFullscreen();
  else (video as HTMLVideoElement & { webkitEnterFullscreen?: () => void }).webkitEnterFullscreen?.();
}

async function togglePip() {
  try {
    if (document.pictureInPictureElement) await document.exitPictureInPicture();
    else await video.requestPictureInPicture();
  } catch {
    /* not allowed here */
  }
}

function syncButtons() {
  const p = btn('play');
  p.innerHTML = video.paused ? icons.play : icons.pause;
  p.setAttribute('aria-label', video.paused ? 'Play' : 'Pause');
  const m = btn('mute');
  m.innerHTML = video.muted || video.volume === 0 ? icons.muted : video.volume < 0.5 ? icons.volumeLow : icons.volume;
  m.setAttribute('aria-label', video.muted ? 'Unmute' : 'Mute');
  const v = video.muted ? 0 : video.volume;
  slider.value = String(v);
  slider.style.setProperty('--v', `${v * 100}%`);
  if (!video.muted) root.classList.remove('is-unmute');
  root.classList.toggle('is-paused', video.paused);
  const fs = !!(document.fullscreenElement || (document as { webkitFullscreenElement?: Element }).webkitFullscreenElement);
  btn('fullscreen').innerHTML = fs ? icons.exitFullscreen : icons.fullscreen;
}
document.addEventListener('fullscreenchange', syncButtons);
if (!document.pictureInPictureEnabled) btn('pip').hidden = true;
else video.removeAttribute('disablepictureinpicture');

slider.addEventListener('input', () => {
  video.volume = Number(slider.value);
  video.muted = video.volume === 0;
  savePrefs();
});

root.addEventListener('click', (ev) => {
  const t = ev.target as HTMLElement;
  const act = t.closest<HTMLElement>('[data-act]')?.dataset.act;
  if (act) {
    ev.stopPropagation();
    if (act === 'play') togglePlay();
    else if (act === 'mute') toggleMute();
    else if (act === 'golive') goLive();
    else if (act === 'fullscreen') toggleFullscreen();
    else if (act === 'pip') void togglePip();
    else if (act === 'quality') toggleMenu();
    else if (act === 'chat') chat.toggle(true);
    uiPing();
    return;
  }
  if (t.closest('.jp-menu')) return;
  closeMenu();
  if (t.closest('.jp-unmute')) return toggleMute();
  if (t.closest('.jp-bigplay')) return togglePlay();
  if (t.closest('.jp-hit') && view === 'live') {
    // Mouse: click toggles playback. Touch: first tap shows the controls.
    if (lastPointer === 'mouse' || root.classList.contains('ui-on')) {
      if (lastPointer === 'mouse') togglePlay();
      else root.classList.contains('ui-on') ? uiHide() : uiPing();
    } else uiPing();
  }
});
$('.jp-hit').addEventListener('dblclick', () => view === 'live' && toggleFullscreen());

document.addEventListener('keydown', (e) => {
  if (view !== 'live' || e.metaKey || e.ctrlKey || e.altKey) return;
  // Typing in chat is not a shortcut.
  if ((e.target as HTMLElement | null)?.closest?.('input, textarea, [contenteditable]')) return;
  const k = e.key.toLowerCase();
  if (k === ' ' || k === 'k') togglePlay();
  else if (k === 'm') toggleMute();
  else if (k === 'f') toggleFullscreen();
  else if (k === 'l' || k === 'end') goLive();
  else if (k === 'arrowup' || k === 'arrowdown') {
    video.muted = false;
    video.volume = Math.min(1, Math.max(0, video.volume + (k === 'arrowup' ? 0.1 : -0.1)));
    savePrefs();
  } else return;
  e.preventDefault();
  uiPing();
});

// Controls show on movement and fade after a moment of stillness while playing.
let lastPointer = 'mouse';
let uiTimer = 0;
function uiPing() {
  root.classList.add('ui-on');
  clearTimeout(uiTimer);
  uiTimer = window.setTimeout(uiHide, lastPointer === 'mouse' ? 2600 : 3600);
}
function uiHide() {
  if (video.paused || root.classList.contains('menu-open') || root.matches(':has(.jp-bar:hover)')) return;
  root.classList.remove('ui-on');
}
root.addEventListener('pointermove', (e) => {
  lastPointer = e.pointerType;
  if (e.pointerType === 'mouse') uiPing();
});
root.addEventListener('pointerdown', (e) => (lastPointer = e.pointerType));
root.addEventListener('pointerleave', () => {
  if (!video.paused) root.classList.remove('ui-on');
});

// ---------------------------------------------------------------- quality
function levelsByHeight(): { index: number; level: Level }[] {
  if (!hls) return [];
  const seen = new Set<number>();
  return hls.levels
    .map((level, index) => ({ index, level }))
    .sort((a, b) => b.level.height - a.level.height || b.level.bitrate - a.level.bitrate)
    .filter(({ level }) => (seen.has(level.height) ? false : (seen.add(level.height), true)));
}

function buildQualityMenu() {
  const items = levelsByHeight();
  btn('quality').parentElement!.hidden = items.length < 2;
  const auto = hls?.autoLevelEnabled ?? true;
  const current = hls && !auto ? hls.nextLevel : -1;
  menu.innerHTML =
    `<div class="jp-menu-h">Quality</div>` +
    [{ index: -1, label: 'Auto' }, ...items.map(({ index, level }) => ({ index, label: label(level) }))]
      .map(
        (it) =>
          `<button type="button" role="menuitemradio" aria-checked="${it.index === current}" data-level="${it.index}">${icons.check}<span>${it.label}</span></button>`,
      )
      .join('');
  updateQualityLabel();
}

function label(l: Level): string {
  return l.height ? `${l.height}p` : `${Math.round(l.bitrate / 1000)} kbps`;
}

function updateQualityLabel() {
  if (!hls) return;
  const lvl = hls.levels[hls.currentLevel];
  const auto = hls.autoLevelEnabled;
  qLabel.textContent = lvl ? (auto ? `Auto · ${label(lvl)}` : label(lvl)) : 'Auto';
  root.classList.toggle('is-hd', !!lvl && lvl.height >= 720);
  menu.querySelectorAll<HTMLButtonElement>('[data-level]').forEach((b) => {
    const i = Number(b.dataset.level);
    b.setAttribute('aria-checked', String(auto ? i === -1 : i === hls!.currentLevel));
    if (auto && i === -1 && lvl) b.querySelector('span')!.innerHTML = `Auto <small>${label(lvl)}</small>`;
  });
}

menu.addEventListener('click', (e) => {
  const b = (e.target as HTMLElement).closest<HTMLButtonElement>('[data-level]');
  if (!b || !hls) return;
  hls.nextLevel = Number(b.dataset.level); // -1 = automatic
  closeMenu();
  window.setTimeout(updateQualityLabel, 0);
});
function toggleMenu() {
  root.classList.contains('menu-open') ? closeMenu() : openMenu();
}
function openMenu() {
  root.classList.add('menu-open');
  btn('quality').setAttribute('aria-expanded', 'true');
}
function closeMenu() {
  root.classList.remove('menu-open');
  btn('quality').setAttribute('aria-expanded', 'false');
}

// ---------------------------------------------------------------- clock + live badge
window.setInterval(() => {
  if (view !== 'live') return;
  if (embed?.went_live_at) clock.textContent = hms((Date.now() - Date.parse(embed.went_live_at)) / 1000);
  root.classList.toggle('is-behind', hasPlayed && behindLive() > 8);
}, 500);

function hms(s: number): string {
  s = Math.max(0, Math.floor(s));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

/** Readable text colour on a custom accent. */
function inkFor(hex: string): string {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255).map((c) =>
    c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4,
  );
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.35 ? '#0e1626' : '#ffffff';
}

// ---------------------------------------------------------------- members
interface MemberSession {
  token: string;
  u: number;
  expires_at: string;
  viewer: { id: string; name: string | null; role: 'viewer' | 'moderator' } | null;
}
interface MemberPlayback {
  url: string | null;
  replay_url: string | null;
  expires_at: string;
}

/**
 * A viewer the site signed in (#vt=, docs: /viewer-tokens). The token opens
 * one session per stream: who they are (chat, attendance) and, on a
 * members-only stream, their own playback URLs, renewed a minute before they
 * run out. A refused renewal (removed, or the token's exp passed) ends
 * playback at once.
 */
const member = (() => {
  let session: MemberSession | null = null;
  let sessionFor = '';
  let moderatorToken: string | null = null;
  let playback: MemberPlayback | null = null;
  let refused: string | null = null;
  let renewTimer = 0;
  let signedInFor = '';
  const key = () => `jc.session.${streamId}`;

  function keep(s: MemberSession) {
    session = s;
    sessionFor = streamId;
    try {
      localStorage.setItem(key(), JSON.stringify({ token: s.token, u: s.u, expires_at: s.expires_at, viewer: s.viewer }));
    } catch {
      /* this page only */
    }
  }

  function stored(): MemberSession | null {
    try {
      const s = JSON.parse(localStorage.getItem(key()) ?? 'null') as MemberSession | null;
      return s?.viewer && Date.parse(s.expires_at) > Date.now() + 60_000 ? s : null;
    } catch {
      return null;
    }
  }

  function setPlayback(p: MemberPlayback | null) {
    playback = p;
    clearTimeout(renewTimer);
    if (!p) return;
    const wait = Math.max(10_000, Date.parse(p.expires_at) - Date.now() - 60_000);
    renewTimer = window.setTimeout(() => void renew(), wait);
  }

  async function signIn(): Promise<void> {
    signedInFor = streamId;
    try {
      const r = await fetch(`${API}/v1/embed/${streamId}/session`, {
        method: 'POST',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ viewer_token: viewerToken }),
      });
      const body = await r.json().catch(() => null);
      if (!r.ok || !body) return;
      keep(body as MemberSession);
      moderatorToken = body.moderator?.token ?? null;
      setPlayback(body.playback ?? null);
    } catch {
      /* the page shows "members only" or plays anonymously */
    }
  }

  async function renew(): Promise<void> {
    if (!session) return;
    try {
      const r = await fetch(`${API}/v1/embed/${streamId}/playback`, {
        method: 'POST',
        credentials: 'omit',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: session.token }),
      });
      const body = await r.json().catch(() => null);
      if (r.ok && body) {
        const old = playback?.url;
        setPlayback(body.playback ?? null);
        // Safari's own HLS can't swap the token per request: reload with the new URL.
        if (native && playback?.url && playback.url !== old && view === 'live') {
          video.src = playback.url;
          void video.play().catch(() => undefined);
        }
        return;
      }
      if (r.status === 403 || r.status === 401) {
        refused = body?.error?.code ?? 'refused';
        setPlayback(null);
        if (embed?.access === 'viewer_token') {
          teardown();
          beacon.stop();
          setView('removed');
        }
        return;
      }
    } catch {
      /* network: try again shortly, the current URL still works for a while */
    }
    renewTimer = window.setTimeout(() => void renew(), 15_000);
  }

  return {
    get session() {
      return sessionFor === streamId ? session : null;
    },
    get moderatorToken() {
      return moderatorToken;
    },
    get playback() {
      return playback;
    },
    get refused() {
      return refused;
    },
    refresh: () => renew(),
    /** Once per stream: sign in with the site's token, or pick up this browser's earlier session. */
    async prepare(e: Embed) {
      if (refused || !e.id.startsWith('str_')) return;
      if (viewerToken && signedInFor !== streamId) return signIn();
      if (sessionFor !== streamId) {
        const s = stored();
        if (s) {
          session = s;
          sessionFor = streamId;
          if (e.access === 'viewer_token') await renew();
        }
      }
    },
  };
})();

/** The current token for this viewer's request: the newest URL's, whatever the playlist said. */
const SIGNED = /^(https?:\/\/[^/]+\/bcdn_token=[^/]*)(\/.*)$/;
function swapToken(url: string): string {
  const now = member.playback?.url?.match(SIGNED)?.[1];
  const m = url.match(SIGNED);
  return now && m ? now + m[2] : url;
}
type LoaderClass = NonNullable<HlsConfig['loader']>;
function tokenLoader(Base: LoaderClass): LoaderClass {
  return class extends Base {
    load(...args: Parameters<InstanceType<LoaderClass>['load']>) {
      args[0].url = swapToken(args[0].url);
      super.load(...args);
    }
  };
}

// ---------------------------------------------------------------- viewer beacon
/**
 * Sampled "I'm here" reports (claude/viewers-plan.md). This tab draws u once;
 * it reports every `interval` seconds only while u < the rate the status read
 * gives it, so a stream's reports stay near a fixed number however large the
 * audience. Nothing personal is sent: a random id for this page load, the
 * state, and u.
 */
const beacon = (() => {
  const u = Math.random();
  const sid = Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => 'abcdefghijklmnopqrstuvwxyz0123456789'[b % 36]).join('');
  let rate = 0;
  let interval = 30;
  let timer = 0;
  let active = false;

  function state(): string {
    if (view === 'live') {
      if (root.classList.contains('is-spinning') || root.classList.contains('is-notice')) return 'b';
      return video.paused ? 'z' : 'p';
    }
    return 'w'; // loading, pending, starting: in the waiting room
  }

  function send() {
    if (!active) return;
    // Signed in by the site: the session's own token, every beat (attendance).
    const s = member.session;
    if (!s?.viewer && u >= rate) return;
    const url = s?.viewer
      ? `${API}/v1/embed/${streamId}/beat?t=${encodeURIComponent(s.token)}&st=${state()}`
      : `${API}/v1/embed/${streamId}/beat?s=${sid}&st=${state()}&u=${u.toFixed(6)}`;
    try {
      if (!navigator.sendBeacon?.(url)) void fetch(url, { method: 'POST', keepalive: true, mode: 'no-cors', credentials: 'omit' });
    } catch {
      /* counting is best-effort */
    }
  }

  function loop(delayS: number) {
    clearTimeout(timer);
    timer = window.setTimeout(() => {
      send();
      loop(interval * (0.9 + Math.random() * 0.2)); // jitter, so tabs opened together drift apart
    }, delayS * 1000);
  }

  return {
    stop() {
      active = false;
      clearTimeout(timer);
    },
    update(e: Embed) {
      if (!e.beacon) {
        active = false;
        clearTimeout(timer);
        return;
      }
      rate = e.beacon.rate;
      interval = e.beacon.interval_seconds;
      if (!active) {
        active = true;
        loop(Math.random() * 5); // first report within 5 s, spread out
      }
    },
  };
})();

// ---------------------------------------------------------------- chat
/**
 * Loads the chat chunk the first time the stream turns out to have chat, and
 * lays the page out around it. Hidden again when the owner turns chat off,
 * or once it has closed after the end.
 */
const chat = (() => {
  type Handle = import('./chat').ChatHandle;
  let handle: Handle | null = null;
  let loading = false;
  const html = document.documentElement;
  let host: HTMLElement | null = null;

  function wanted(e: Embed): boolean {
    if (opts.chat === '0' || !e.chat) return false;
    // Closed after the end (not kept visible): nothing to show, give the video its room back.
    return !(e.chat.state === 'closed' && e.status !== 'pending');
  }

  return {
    update(e: Embed) {
      if (!wanted(e)) {
        if (handle) {
          handle.destroy();
          handle = null;
          html.classList.remove('with-chat', 'chat-open');
        }
        return;
      }
      if (handle || loading) return;
      loading = true;
      void import('./chat')
        .then((m) => {
          host ??= Object.assign(document.createElement('aside'), { id: 'jc-host' });
          document.body.append(host);
          html.classList.add('with-chat', framed ? 'chat-embed' : 'chat-page');
          if (framed && opts.chat === '1') html.classList.add('chat-open');
          handle = m.startChat({
            streamId,
            api: API,
            read: EMBED_READ,
            host,
            reactionBar: $<HTMLElement>('.jp-reactbar'),
            floatLayer: $<HTMLElement>('.jp-floatlayer'),
            reactions: opts.reactions,
            clock: pictureClock,
            // Signed in by the site: chat uses the same session (and moderator session).
            session: member.session,
            moderatorToken: member.moderatorToken,
            viewerToken: member.session ? null : viewerToken,
            lang: opts.lang,
            accent: opts.accent ? { color: opts.accent, ink: inkFor(opts.accent) } : null,
            closable: framed,
            onClose: () => chat.toggle(false),
            isPending: () => embed?.status === 'pending',
          });
          $('.jp-chatbtn span').textContent = handle.copy.open;
        })
        .catch(() => undefined)
        .finally(() => (loading = false));
    },
    toggle(open: boolean) {
      html.classList.toggle('chat-open', open);
      if (open) window.setTimeout(() => handle?.panel.focus(), 300);
    },
  };
})();

/**
 * The EXT-X-PROGRAM-DATE-TIME of the frame on screen, in ms: when it was
 * live at the origin. Reactions are placed and drawn on this clock. Null
 * when no live picture is moving (paused, starting, ended, replay).
 */
function pictureClock(): number | null {
  if (view !== 'live' || video.paused) return null;
  const d = hls?.playingDate;
  if (d) return d.getTime();
  // Safari's own HLS: the playlist's date at currentTime 0.
  const start = (video as HTMLVideoElement & { getStartDate?: () => Date }).getStartDate?.().getTime();
  return start !== undefined && Number.isFinite(start) && start > 0 ? start + video.currentTime * 1000 : null;
}

// Local checks (.joa-logs/reactions-check) read the clock; never in a build.
if (import.meta.env.DEV) Object.assign(window, { __joa: { clock: pictureClock, view: () => view, hls: () => hls, renew: () => member.refresh() } });

// ---------------------------------------------------------------- replay
/**
 * After the end, with replay on: the recording, an MP4, in the same video
 * element with the browser's own controls (a seek bar, which a live player
 * has no use for). No counting, no live UI; the API keeps being asked only
 * on a channel page, where the next stream takes over when it starts.
 */
function startReplay() {
  const r = embed?.replay;
  const url = r?.url ?? member.playback?.replay_url;
  if (!r || !url) return;
  teardown();
  clearTimeout(stallTimer);
  beacon.stop();
  setView('replay');
  video.controls = true;
  video.muted = prefs.muted;
  video.volume = prefs.volume;
  video.src = url;
  void video.play().catch(() => undefined);
}
$('.jp-replay').addEventListener('click', startReplay);

// ---------------------------------------------------------------- go
syncButtons();
void tick();
